import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, ChevronDown } from 'lucide-react';
import { useConsultation } from '@/components/ConsultationModal';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const EVENT_LINK = 'https://linktr.ee/theconnectionstrategist?utm_source=ig&utm_medium=social&utm_content=link_in_bio&fbclid=PAZXh0bgNhZW0CMTEAc3J0YwZhcHBfaWQMMjU2MjgxMDQwNTU4AAGnVMEwnjfbNw4sQOhB1BfY_7F3JrSWYmL65ruc-Kiywx5Eb_lIjzp_vNATPNA_aem_0TpLAo0FCXpv-404zbFcmQ';
const IT_LINK = 'https://solacium.us/';

const ASSOCIATE_SUBMENU = [
  { label: 'Bookkeeping', path: '/services', external: false },
  { label: 'Vintage Wine & Champagne', path: '/wine', external: false },
  { label: 'Web Design', path: '/web-design', external: false },
  { label: 'Events & Vendor Connections', path: EVENT_LINK, external: true },
  { label: 'IT & Programming', path: IT_LINK, external: true },
];

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Bookkeeping Services', path: '/services' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const { setOpen } = useConsultation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAssocOpen, setMobileAssocOpen] = useState(false);
  const isAssocActive = pathname === '/associate-network' || pathname === '/wine' || pathname === '/web-design';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-wc-border" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex flex-col" data-testid="navbar-logo">
          <span className="font-accent text-xs uppercase tracking-[0.2em] text-wc-black font-semibold">
            William Covington
          </span>
          <span className="font-accent text-[10px] uppercase tracking-[0.2em] text-wc-muted">
            & Associates
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="navbar-links">
          {NAV_LINKS.slice(0, 3).map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-body text-sm transition-colors duration-300 ${
                pathname === link.path
                  ? 'text-wc-black border-b border-wc-gold pb-0.5'
                  : 'text-wc-muted hover:text-wc-black'
              }`}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </Link>
          ))}

          {/* Associate Network with dropdown */}
          <div className="relative group" data-testid="nav-associate-dropdown">
            <Link
              to="/associate-network"
              className={`font-body text-sm transition-colors duration-300 flex items-center gap-1 ${
                isAssocActive
                  ? 'text-wc-black border-b border-wc-gold pb-0.5'
                  : 'text-wc-muted hover:text-wc-black'
              }`}
              data-testid="nav-link-associate-network"
            >
              Associate Network
              <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" strokeWidth={1.5} />
            </Link>
            <div className="absolute top-full left-0 pt-2 hidden group-hover:block">
              <div className="bg-white border border-wc-border min-w-[240px] py-2" data-testid="associate-dropdown-menu">
                {ASSOCIATE_SUBMENU.map((item) => (
                  item.external ? (
                    <a
                      key={item.label}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-5 py-2.5 font-body text-sm text-wc-muted hover:text-wc-black hover:bg-wc-bg-alt transition-colors duration-200"
                      data-testid={`dropdown-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                      <ArrowUpRight className="w-3 h-3 opacity-40" strokeWidth={1.5} />
                    </a>
                  ) : (
                    <Link
                      key={item.label}
                      to={item.path}
                      className="block px-5 py-2.5 font-body text-sm text-wc-muted hover:text-wc-black hover:bg-wc-bg-alt transition-colors duration-200"
                      data-testid={`dropdown-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>

          <Link
            to="/contact"
            className={`font-body text-sm transition-colors duration-300 ${
              pathname === '/contact'
                ? 'text-wc-black border-b border-wc-gold pb-0.5'
                : 'text-wc-muted hover:text-wc-black'
            }`}
            data-testid="nav-link-contact"
          >
            Contact
          </Link>
        </nav>

        <div className="hidden lg:block">
          <button
            onClick={() => setOpen(true)}
            className="bg-wc-black text-white px-6 py-2.5 font-body text-xs uppercase tracking-[0.15em] hover:bg-wc-charcoal transition-colors duration-300 flex items-center gap-2 group"
            data-testid="navbar-cta"
          >
            Schedule a Consultation
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
          </button>
        </div>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden p-2" data-testid="mobile-menu-trigger">
              <Menu className="w-6 h-6 text-wc-black" strokeWidth={1.5} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 bg-white border-l border-wc-border p-0">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b border-wc-border">
                <span className="font-accent text-xs uppercase tracking-[0.2em] text-wc-black font-semibold">
                  WC&A
                </span>
                <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close">
                  <X className="w-5 h-5 text-wc-black" strokeWidth={1.5} />
                </button>
              </div>
              <nav className="flex flex-col p-6 gap-1" data-testid="mobile-nav">
                {NAV_LINKS.slice(0, 3).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    className={`font-body text-base py-3 border-b border-wc-border transition-colors duration-300 ${
                      pathname === link.path ? 'text-wc-black font-medium' : 'text-wc-muted hover:text-wc-black'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                {/* Associate Network with sub-items */}
                <button
                  onClick={() => setMobileAssocOpen(!mobileAssocOpen)}
                  className={`font-body text-base py-3 border-b border-wc-border transition-colors duration-300 flex items-center justify-between ${
                    isAssocActive ? 'text-wc-black font-medium' : 'text-wc-muted hover:text-wc-black'
                  }`}
                >
                  Associate Network
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileAssocOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                </button>
                {mobileAssocOpen && (
                  <div className="pl-4 space-y-0">
                    <Link to="/associate-network" onClick={() => setMobileOpen(false)} className="block font-body text-sm py-2.5 text-wc-muted hover:text-wc-black border-b border-wc-border/50">
                      Overview
                    </Link>
                    {ASSOCIATE_SUBMENU.map((item) => (
                      item.external ? (
                        <a key={item.label} href={item.path} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="flex items-center justify-between font-body text-sm py-2.5 text-wc-muted hover:text-wc-black border-b border-wc-border/50">
                          {item.label}
                          <ArrowUpRight className="w-3 h-3 opacity-40" strokeWidth={1.5} />
                        </a>
                      ) : (
                        <Link key={item.label} to={item.path} onClick={() => setMobileOpen(false)} className="block font-body text-sm py-2.5 text-wc-muted hover:text-wc-black border-b border-wc-border/50">
                          {item.label}
                        </Link>
                      )
                    ))}
                  </div>
                )}
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  className={`font-body text-base py-3 border-b border-wc-border transition-colors duration-300 ${
                    pathname === '/contact' ? 'text-wc-black font-medium' : 'text-wc-muted hover:text-wc-black'
                  }`}
                >
                  Contact
                </Link>
              </nav>
              <div className="mt-auto p-6">
                <button
                  onClick={() => { setMobileOpen(false); setOpen(true); }}
                  className="w-full bg-wc-black text-white py-3 font-body text-xs uppercase tracking-[0.15em] hover:bg-wc-charcoal transition-colors duration-300"
                  data-testid="mobile-cta"
                >
                  Schedule a Consultation
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
